import showErrorToast from '../../Components/Shared/ShowErrorToast';
import toast from 'react-hot-toast';
import { Services } from '../../types/services';
import { Dispatch } from 'redux';
import axios, { Canceler } from 'axios';

// Action Types
const DAYLIGHT_MARK_CODE_COMPLETE = 'DAYLIGHT_MARK_CODE_COMPLETE';
const DAYLIGHT_UNMARK_CODE_COMPLETE = 'DAYLIGHT_UNMARK_CODE_COMPLETE';
const DAYLIGHT_MARK_CODE_MAPPING_FOR_REVIEW = 'DAYLIGHT_MARK_CODE_MAPPING_FOR_REVIEW';
const DAYLIGHT_REMOVE_CODE_MAPPING = 'DAYLIGHT_REMOVE_CODE_MAPPING';
const DAYLIGHT_UNMARK_CODE_MAPPING_FOR_REVIEW = 'DAYLIGHT_UNMARK_CODE_MAPPING_FOR_REVIEW';
const SET_DAYLIGHT_CODES = 'SET_DAYLIGHT_CODES';
const SET_INCOMING_NEO_CODES = 'SET_INCOMING_NEO_CODES';
const TOGGLE_DAYLIGHT_CODE_REVIEW_MODAL = 'TOGGLE_DAYLIGHT_CODE_REVIEW_MODAL';
const SET_DAYLIGHT_WORKING_PRACTICE = 'SET_DAYLIGHT_WORKING_PRACTICE';
const SET_DAYLIGHT_IS_LOADING_CODES = 'SET_DAYLIGHT_IS_LOADING_CODES';
const SET_DAYLIGHT_PRACTICE_FINALIZED_STATUS = 'SET_DAYLIGHT_PRACTICE_FINALIZED_STATUS';
const SET_DAYLIGHT_CODES_FILTER_CODE_NAME = 'SET_DAYLIGHT_CODES_FILTER_CODE_NAME';
const SET_DAYLIGHT_CODES_FILTER_CODE_CATEGORY = 'SET_DAYLIGHT_CODES_FILTER_CODE_CATEGORY';
const SET_DAYLIGHT_CODES_FILTER_CODE_DESCRIPTION = 'SET_DAYLIGHT_CODES_FILTER_CODE_DESCRIPTION';
const SET_DAYLIGHT_CODES_FILTER_CODE_MAPPING = 'SET_DAYLIGHT_CODES_FILTER_CODE_MAPPING';
const SET_DAYLIGHT_CODES_FILTER_CODE_HAS_COMMENTS = 'SET_DAYLIGHT_CODES_FILTER_CODE_HAS_COMMENTS';
const SET_DAYLIGHT_CODES_FILTER_CODE_IS_MARKED_COMPLETED =
  'SET_DAYLIGHT_CODES_FILTER_CODE_IS_MARKED_COMPLETED';
const SET_DAYLIGHT_CODES_FILTER_CODE_NEEDS_REVIEW = 'SET_DAYLIGHT_CODES_FILTER_CODE_NEEDS_REVIEW';
const SET_DAYLIGHT_CODES_FILTER_MARKED_DISABLED = 'SET_DAYLIGHT_CODES_FILTER_MARKED_DISABLED';
const SET_DAYLIGHT_CODES_FILTER_CODE_LOCATION = 'SET_DAYLIGHT_CODES_FILTER_CODE_LOCATION';
const SET_DAYLIGHT_UPDATING_CODE_ID = 'SET_DAYLIGHT_UPDATING_CODE_ID';
const SET_DAYLIGHT_UPDATING_MAPPING_ID = 'SET_DAYLIGHT_UPDATING_MAPPING_ID';
const SET_DAYLIGHT_TARGET_CODE_FOR_ACTION = 'SET_DAYLIGHT_TARGET_CODE_FOR_ACTION';
const SET_DAYLIGHT_ADD_MAPPING_MODAL_OPEN = 'SET_DAYLIGHT_ADD_MAPPING_MODAL_OPEN';
const SET_DAYLIGHT_EDIT_MAPPING_MODAL_OPEN = 'SET_DAYLIGHT_EDIT_MAPPING_MODAL_OPEN';
const SET_DAYLIGHT_TARGET_MAPPING_FOR_ACTION = 'SET_DAYLIGHT_TARGET_MAPPING_FOR_ACTION';
const SET_DAYLIGHT_SELECTED_CODE_USAGE_DETAIL = 'SET_DAYLIGHT_SELECTED_CODE_USAGE_DETAIL';
const SET_DAYLIGHT_USER_HAS_ACCESS_TO_USAGE_DETAILS =
  'SET_DAYLIGHT_USER_HAS_ACCESS_TO_USAGE_DETAILS';
const SET_DAYLIGHT_MAPPING_COMMENT_MODAL_OPEN = 'SET_DAYLIGHT_MAPPING_COMMENT_MODAL_OPEN';
const ADD_DAYLIGHT_MAPPING = 'ADD_DAYLIGHT_MAPPING';
const SET_DAYLIGHT_MAPPING = 'SET_DAYLIGHT_MAPPING';
const SET_DAYLIGHT_MAPPING_COMMENT = 'SET_DAYLIGHT_MAPPING_COMMENT';
const SET_DAYLIGHT_CODES_DISPLAYED_COUNT = 'SET_DAYLIGHT_CODES_DISPLAYED_COUNT';
const SET_DAYLIGHT_CODE = 'SET_DAYLIGHT_CODE';
const SET_DAYLIGHT_SELECTED_PARTNER_ID = 'SET_DAYLIGHT_SELECTED_PARTNER_ID';
const CLEAR_SKIP_CODES_TABLE_SCROLL_TO_TOP = 'CLEAR_SKIP_CODES_TABLE_SCROLL_TO_TOP';
const SELECT_DAYLIGHT_CODE = 'SELECT_DAYLIGHT_CODE';
const DESELECT_DAYLIGHT_CODE = 'DESELECT_DAYLIGHT_CODE';
const SELECT_ALL_FILTERED_CODES = 'SELECT_ALL_FILTERED_CODES';
const CLEAR_CODE_SELECTION = 'CLEAR_CODE_SELECTION';
const SET_BULK_ACTION_IN_PROGRESS = 'SET_BULK_ACTION_IN_PROGRESS';
const SET_BULK_ADD_MAPPING_MODAL_OPEN = 'SET_BULK_ADD_MAPPING_MODAL_OPEN';
const SET_DAYLIGHT_SORT = 'SET_DAYLIGHT_SORT';

// Interfaces
interface Mapping {
  id: string;
  taxonomyCodeNodeGuid?: string;
  comments?: string;
  [key: string]: unknown;
}

interface Code {
  id: string;
  code?: string;
  codeDescription?: string;
  needsReview?: boolean;
  confirmationStatus?: number;
  mappings: Mapping[];
  [key: string]: unknown;
}

interface Practice {
  id: string;
  name: string;
}

type SortField =
  | 'codeCategory'
  | 'code'
  | 'codeDescription'
  | 'codePrice'
  | 'locationId'
  | 'codeTTM'
  | 'usageDetails'
  | 'mappings'
  | null;
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface State {
  practiceIsFinalized: boolean;
  daylightCodes: Code[];
  daylightCodeCount: number;
  daylightTotalCodesCount: number;
  daylightCodeCategories: string[];
  daylightLocations: string[];
  incomingNeoCodes: unknown[];
  daylightCodeModalVisible: boolean;
  practice: Practice;
  isLoadingCodes: boolean;
  codeNameFilter: string;
  codeCategoryFilter: string;
  codeDescriptionFilter: string;
  codeLocationFilter: string;
  codeMappingFilter: string;
  codeHasCommentsFilter: boolean | null;
  codeIsMarkedCompletedFilter: boolean | null;
  codeNeedsReviewFilter: boolean | null;
  codeMarkedDisabledFilter: boolean | null;
  actionTargetCode: Code | Record<string, never>;
  updatingCodeId: string;
  updatingMappingId: string;
  actionTargetMapping: Mapping | Record<string, never>;
  isAddMappingModalOpen: boolean;
  isEditMappingModalOpen: boolean;
  selectedCodeUsageDetail: string;
  userHasAccessToCodeUsageDetails: boolean;
  isMappingCommentModalOpen: boolean;
  partnerIdFilter: string | null;
  skipCodesTableScrollToTop: boolean;
  selectedCodeIds: string[];
  isBulkActionInProgress: boolean;
  isBulkAddMappingModalOpen: boolean;
  lastSelectedCodeId: string | null;
  sortConfig: SortConfig | null;
}

interface Action {
  type: string;
  payload?: unknown;
  status?: boolean;
  codes?: unknown;
  targetCodeId?: string;
  targetCode?: Code;
  targetMapping?: Mapping;
  targetMappingId?: string;
}

const initialState: State = {
  practiceIsFinalized: false,
  daylightCodes: [],
  daylightCodeCount: 0,
  daylightTotalCodesCount: 0,
  daylightCodeCategories: [],
  daylightLocations: [],
  incomingNeoCodes: [],
  daylightCodeModalVisible: false,
  practice: { id: '', name: '' },
  isLoadingCodes: false,
  codeNameFilter: '',
  codeCategoryFilter: '',
  codeDescriptionFilter: '',
  codeLocationFilter: '',
  codeMappingFilter: '',
  codeHasCommentsFilter: null,
  codeIsMarkedCompletedFilter: null,
  codeNeedsReviewFilter: null,
  codeMarkedDisabledFilter: null,
  actionTargetCode: {},
  updatingCodeId: '',
  updatingMappingId: '',
  actionTargetMapping: {},
  isAddMappingModalOpen: false,
  isEditMappingModalOpen: false,
  selectedCodeUsageDetail: 'frequency6Month',
  userHasAccessToCodeUsageDetails: false,
  isMappingCommentModalOpen: false,
  partnerIdFilter: null,
  skipCodesTableScrollToTop: false,
  selectedCodeIds: [],
  isBulkActionInProgress: false,
  isBulkAddMappingModalOpen: false,
  lastSelectedCodeId: null,
  sortConfig: null,
};

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case SET_DAYLIGHT_WORKING_PRACTICE:
      return {
        ...state,
        practice: action.payload as Practice,
      };
    case SET_DAYLIGHT_UPDATING_CODE_ID:
      return {
        ...state,
        updatingCodeId: action.payload as string,
      };
    case SET_DAYLIGHT_UPDATING_MAPPING_ID:
      return {
        ...state,
        updatingMappingId: action.payload as string,
      };
    case TOGGLE_DAYLIGHT_CODE_REVIEW_MODAL:
      return {
        ...state,
        daylightCodeModalVisible: action.status as boolean,
      };
    case DAYLIGHT_MARK_CODE_MAPPING_FOR_REVIEW:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return { ...code, needsReview: true };
          }
          return code;
        }),
      };
    case DAYLIGHT_UNMARK_CODE_MAPPING_FOR_REVIEW:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return { ...code, needsReview: false };
          }
          return code;
        }),
      };
    case DAYLIGHT_REMOVE_CODE_MAPPING: {
      const newCode = {
        ...action.targetCode!,
        mappings: action.targetCode!.mappings.filter(
          mapping => mapping.id !== action.targetMapping?.id,
        ),
      };
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCode?.id) {
            return newCode;
          }
          return code;
        }),
      };
    }
    case DAYLIGHT_MARK_CODE_COMPLETE:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return { ...code, confirmationStatus: 1 };
          }
          return code;
        }),
      };
    case DAYLIGHT_UNMARK_CODE_COMPLETE:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return { ...code, confirmationStatus: 0 };
          }
          return code;
        }),
      };
    case SET_DAYLIGHT_CODES: {
      const codes = action.codes as
        | {
            isFinalized?: boolean;
            codeMappings?: Code[];
            totalCount?: number;
            categories?: string[];
            locations?: string[];
          }
        | undefined;
      return {
        ...state,
        skipCodesTableScrollToTop: false,
        practiceIsFinalized: codes?.isFinalized || false,
        daylightCodes: codes?.codeMappings || [],
        daylightTotalCodesCount: codes?.totalCount || 0,
        daylightCodeCategories: codes?.categories || [],
        daylightLocations: codes?.locations || [],
      };
    }
    case SET_INCOMING_NEO_CODES:
      return {
        ...state,
        incomingNeoCodes: (action.codes as unknown[]) || [],
      };
    case SET_DAYLIGHT_PRACTICE_FINALIZED_STATUS:
      return {
        ...state,
        practiceIsFinalized: action.payload as boolean,
      };
    case SET_DAYLIGHT_IS_LOADING_CODES:
      return {
        ...state,
        isLoadingCodes: action.payload as boolean,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_NAME:
      return {
        ...state,
        codeNameFilter: action.payload as string,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_CATEGORY:
      return {
        ...state,
        codeCategoryFilter: action.payload as string,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_DESCRIPTION:
      return {
        ...state,
        codeDescriptionFilter: action.payload as string,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_MAPPING:
      return {
        ...state,
        codeMappingFilter: action.payload as string,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_HAS_COMMENTS:
      return {
        ...state,
        codeHasCommentsFilter: action.payload as boolean | null,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_IS_MARKED_COMPLETED:
      return {
        ...state,
        codeIsMarkedCompletedFilter: action.payload as boolean | null,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_NEEDS_REVIEW:
      return {
        ...state,
        codeNeedsReviewFilter: action.payload as boolean | null,
      };
    case SET_DAYLIGHT_CODES_FILTER_MARKED_DISABLED:
      return {
        ...state,
        codeMarkedDisabledFilter: action.payload as boolean | null,
      };
    case SET_DAYLIGHT_CODES_FILTER_CODE_LOCATION:
      return {
        ...state,
        codeLocationFilter: action.payload as string,
      };
    case SET_DAYLIGHT_TARGET_CODE_FOR_ACTION:
      return {
        ...state,
        actionTargetCode: action.payload as Code,
      };
    case SET_DAYLIGHT_ADD_MAPPING_MODAL_OPEN:
      return {
        ...state,
        isAddMappingModalOpen: action.payload as boolean,
      };
    case SET_DAYLIGHT_EDIT_MAPPING_MODAL_OPEN:
      return {
        ...state,
        isEditMappingModalOpen: action.payload as boolean,
      };
    case SET_DAYLIGHT_TARGET_MAPPING_FOR_ACTION:
      return {
        ...state,
        actionTargetMapping: action.payload as Mapping,
      };
    case SET_DAYLIGHT_SELECTED_CODE_USAGE_DETAIL:
      return {
        ...state,
        selectedCodeUsageDetail: action.payload as string,
      };
    case SET_DAYLIGHT_USER_HAS_ACCESS_TO_USAGE_DETAILS:
      return {
        ...state,
        userHasAccessToCodeUsageDetails: action.payload as boolean,
      };
    case SET_DAYLIGHT_MAPPING_COMMENT_MODAL_OPEN:
      return {
        ...state,
        isMappingCommentModalOpen: action.payload as boolean,
      };
    case ADD_DAYLIGHT_MAPPING:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return {
              ...code,
              mappings: [...code.mappings, action.payload as Mapping],
            };
          }
          return code;
        }),
      };
    case SET_DAYLIGHT_MAPPING: {
      const payloadMapping = action.payload as Mapping & { oldTaxonomyCodeNodeGuid?: string };
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return {
              ...code,
              mappings: code.mappings.map(mapping => {
                if (mapping.taxonomyCodeNodeGuid === payloadMapping.oldTaxonomyCodeNodeGuid) {
                  return { ...mapping, ...payloadMapping };
                }
                return mapping;
              }),
            };
          }
          return code;
        }),
      };
    }
    case SET_DAYLIGHT_MAPPING_COMMENT:
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === action.targetCodeId) {
            return {
              ...code,
              mappings: code.mappings.map(mapping => {
                if (mapping.id === action.targetMappingId) {
                  return { ...mapping, comments: action.payload as string };
                }
                return mapping;
              }),
            };
          }
          return code;
        }),
      };
    case SET_DAYLIGHT_CODES_DISPLAYED_COUNT:
      return {
        ...state,
        daylightCodeCount: action.payload as number,
      };
    case SET_DAYLIGHT_CODE: {
      const payloadCode = action.payload as Code;
      return {
        ...state,
        skipCodesTableScrollToTop: true,
        daylightCodes: state.daylightCodes.map(code => {
          if (code.id === payloadCode.id) {
            return payloadCode;
          }
          return code;
        }),
      };
    }
    case CLEAR_SKIP_CODES_TABLE_SCROLL_TO_TOP:
      return {
        ...state,
        skipCodesTableScrollToTop: false,
      };
    case SET_DAYLIGHT_SELECTED_PARTNER_ID:
      return {
        ...state,
        partnerIdFilter: action.payload as string | null,
      };
    case SELECT_DAYLIGHT_CODE: {
      const codeId = action.payload as string;
      return {
        ...state,
        selectedCodeIds: state.selectedCodeIds.includes(codeId)
          ? state.selectedCodeIds
          : [...state.selectedCodeIds, codeId],
        lastSelectedCodeId: codeId,
      };
    }
    case DESELECT_DAYLIGHT_CODE:
      return {
        ...state,
        selectedCodeIds: state.selectedCodeIds.filter(id => id !== action.payload),
      };
    case SELECT_ALL_FILTERED_CODES:
      return {
        ...state,
        selectedCodeIds: action.payload as string[],
      };
    case CLEAR_CODE_SELECTION:
      return {
        ...state,
        selectedCodeIds: [],
        lastSelectedCodeId: null,
      };
    case SET_BULK_ACTION_IN_PROGRESS:
      return {
        ...state,
        isBulkActionInProgress: action.payload as boolean,
      };
    case SET_BULK_ADD_MAPPING_MODAL_OPEN:
      return {
        ...state,
        isBulkAddMappingModalOpen: action.payload as boolean,
      };
    case SET_DAYLIGHT_SORT:
      return {
        ...state,
        sortConfig: action.payload as SortConfig | null,
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosCall = services.axios.Axios();

  // Internal helpers
  const setUpdatingCodeId = (codeId = '') => ({
    type: SET_DAYLIGHT_UPDATING_CODE_ID,
    payload: codeId,
  });

  const setUpdatingMappingId = (mappingId = '') => ({
    type: SET_DAYLIGHT_UPDATING_MAPPING_ID,
    payload: mappingId,
  });

  const setIsLoadingCodes = (bool: boolean) => ({
    type: SET_DAYLIGHT_IS_LOADING_CODES,
    payload: bool,
  });

  const setDaylightCodes = (codes: unknown) => ({
    type: SET_DAYLIGHT_CODES,
    codes: codes,
  });

  const setIncomingNeoCodes = (codes: unknown[]) => ({
    type: SET_INCOMING_NEO_CODES,
    codes: codes,
  });

  const getDaylightCodesNoFilters =
    (practiceId: string, partnerId = '') =>
    async () => {
      try {
        let url = `daylight/${practiceId}/allcodes`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        return await axiosCall.get(url);
      } catch (error) {
        console.error((error as Error).message);
      }
    };

  const updateCodeLocal = (code: Code) => ({
    type: SET_DAYLIGHT_CODE,
    payload: code,
  });

  const setPracticeIsFinalized = (bool: boolean) => ({
    type: SET_DAYLIGHT_PRACTICE_FINALIZED_STATUS,
    payload: bool,
  });

  const setDaylightCodeMappingModalOpen = (bool: boolean) => ({
    type: TOGGLE_DAYLIGHT_CODE_REVIEW_MODAL,
    status: bool,
  });

  const displayActionFailedToastForCode = (action: string, code: Code) => {
    const codeName = code.code || 'Unknown';
    const codeDesc = code.codeDescription || 'Unknown';
    showErrorToast(
      `Failed to ${action} for Code: ${codeName}|${codeDesc}. Please refresh the list and try again.`,
    );
  };

  const markCodeCompleteLocal = (targetCodeId: string) => ({
    type: DAYLIGHT_MARK_CODE_COMPLETE,
    targetCodeId,
  });

  const unmarkCodeCompleteLocal = (targetCodeId: string) => ({
    type: DAYLIGHT_UNMARK_CODE_COMPLETE,
    targetCodeId,
  });

  const markCodeForReviewLocal = (targetCodeId: string) => ({
    type: DAYLIGHT_MARK_CODE_MAPPING_FOR_REVIEW,
    targetCodeId,
  });

  const unmarkCodeForReviewLocal = (targetCodeId: string) => ({
    type: DAYLIGHT_UNMARK_CODE_MAPPING_FOR_REVIEW,
    targetCodeId,
  });

  const addDaylightMappingLocal = (targetCodeId: string, payload: Mapping) => ({
    type: ADD_DAYLIGHT_MAPPING,
    targetCodeId,
    payload,
  });

  const editDaylightMappingLocal = (targetCodeId: string, payload: unknown) => ({
    type: SET_DAYLIGHT_MAPPING,
    targetCodeId,
    payload,
  });

  const deleteDaylightMappingLocal = (
    _codes: Code[],
    targetCode: Code,
    targetMapping: Mapping,
  ) => ({
    type: DAYLIGHT_REMOVE_CODE_MAPPING,
    targetCode,
    targetMapping,
  });

  const updateCodeMappingCommentsLocal = (
    targetCodeId: string,
    targetMappingId: string,
    comments: string,
  ) => ({
    type: SET_DAYLIGHT_MAPPING_COMMENT,
    targetCodeId,
    targetMappingId,
    payload: comments,
  });

  // Exported actions
  const refreshCodes = (clearActionTargetCode = true) => {
    return async (dispatch: Dispatch, getState: () => { daylight: State }) => {
      const state = getState();
      const practiceId = state.daylight.practice.id;
      const partnerId = state.daylight.partnerIdFilter;
      try {
        dispatch(setIsLoadingCodes(true));
        const res = await dispatch(
          getDaylightCodesNoFilters(practiceId, partnerId ?? '') as unknown as Action,
        );
        if (!res) {
          dispatch(setIsLoadingCodes(false));
          return;
        }
        const axiosData = (res as unknown as { data: { success?: boolean; data?: unknown } }).data;
        const codes = axiosData?.data ?? axiosData;
        dispatch(setDaylightCodes(codes));
        dispatch(setIsLoadingCodes(false));
        dispatch(setUpdatingCodeId(''));
        if (clearActionTargetCode) dispatch(setActionTargetCode({}));
      } catch (error) {
        dispatch(setIsLoadingCodes(false));
      }
    };
  };

  const clearIncomingNeoCodes = () => ({
    type: SET_INCOMING_NEO_CODES,
    codes: [],
  });

  const clearDaylightCodeMapperState = () => {
    return (dispatch: Dispatch) => {
      dispatch(setDaylightCodes([]));
      dispatch(clearIncomingNeoCodes());
      dispatch(setCodeNameFilter(''));
      dispatch(setCodeCategoryFilter(''));
      dispatch(setCodeDescriptionFilter(''));
      dispatch(setCodeMappingFilter(''));
      dispatch({ type: CLEAR_CODE_SELECTION });
      dispatch({ type: SET_BULK_ADD_MAPPING_MODAL_OPEN, payload: false });
    };
  };

  const setCodeNameFilter = (string = '') => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_NAME,
    payload: string,
  });

  const setCodeCategoryFilter = (string = '') => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_CATEGORY,
    payload: string,
  });

  const setCodeDescriptionFilter = (string = '') => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_DESCRIPTION,
    payload: string,
  });

  const setCodeLocationFilter = (string = '') => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_LOCATION,
    payload: string,
  });

  const setCodeMappingFilter = (string = '') => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_MAPPING,
    payload: string,
  });

  const setCodeHasCommentsFilter = (state: boolean | null = null) => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_HAS_COMMENTS,
    payload: state,
  });

  const setCodeIsMarkedCompletedFilter = (state: boolean | null = null) => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_IS_MARKED_COMPLETED,
    payload: state,
  });

  const setCodeNeedsReviewFilter = (state: boolean | null = null) => ({
    type: SET_DAYLIGHT_CODES_FILTER_CODE_NEEDS_REVIEW,
    payload: state,
  });

  const setDaylightCodeMapperPractice = (practiceId = '', practiceName = '') => ({
    type: SET_DAYLIGHT_WORKING_PRACTICE,
    payload: { id: practiceId, name: practiceName },
  });

  const toggleDaylightCodeReviewModal = (status = false) => {
    return async (dispatch: Dispatch) => {
      if (status) {
        dispatch(refreshCodes() as unknown as Action);
        dispatch(fetchIncomingNeoCodes() as unknown as Action);
      }
      dispatch(setDaylightCodeMappingModalOpen(status));
    };
  };

  const CancelToken = axios.CancelToken;
  let cancelIncomingNeoCodesRequest: Canceler | undefined;
  const fetchIncomingNeoCodes = () => {
    return async (dispatch: Dispatch, getState: () => { daylight: State }) => {
      try {
        // Cancel any pending request
        if (cancelIncomingNeoCodesRequest !== undefined) {
          cancelIncomingNeoCodesRequest();
        }
        const state = getState();
        const partnerId = state.daylight.partnerIdFilter;
        let url = 'daylight/incomingnodes/codes';
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        const response = await axiosCall.get<{ data: unknown[] }>(url, {
          cancelToken: new CancelToken(function executor(c) {
            cancelIncomingNeoCodesRequest = c;
          }),
        });
        dispatch(setIncomingNeoCodes(response.data.data));
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        const err = error as { response?: { data: { message: string } } };
        if (
          err.response?.data.message
            .toLowerCase()
            .indexOf('missing partner taxonomy configuration') !== -1
        ) {
          showErrorToast('Missing partner configuration. Please contact support.');
        }
      }
    };
  };

  const setActionTargetCode = (code: Code | Record<string, never> = {}) => ({
    type: SET_DAYLIGHT_TARGET_CODE_FOR_ACTION,
    payload: code,
  });

  const setActionTargetMapping = (mapping: Mapping | Record<string, never> | null = {}) => ({
    type: SET_DAYLIGHT_TARGET_MAPPING_FOR_ACTION,
    payload: mapping || {},
  });

  const setAddMappingModalOpen = (bool = false) => ({
    type: SET_DAYLIGHT_ADD_MAPPING_MODAL_OPEN,
    payload: bool,
  });

  const addDaylightMapping = (
    practiceId: string,
    partnerId: string | null,
    code: Code,
    payload: Mapping,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        const codeId = code.id;
        dispatch(addDaylightMappingLocal(codeId, payload));
        dispatch(setAddMappingModalOpen(false));

        let url = `daylight/${practiceId}/codes/${codeId}/add`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        const res = await axiosCall.post(url, payload);

        const updatedCode = JSON.parse(JSON.stringify(code));
        updatedCode.mappings.push((res.data as unknown as { data: Mapping }).data);
        dispatch(updateCodeLocal(updatedCode));
      } catch (error) {
        displayActionFailedToastForCode('add mapping', code);
        dispatch(updateCodeLocal(code));
      }
    };
  };

  const setEditMappingModalOpen = (bool = false) => ({
    type: SET_DAYLIGHT_EDIT_MAPPING_MODAL_OPEN,
    payload: bool,
  });

  const editDaylightMapping = (
    practiceId: string,
    partnerId: string | null,
    code: Code,
    mappingId: string,
    payload: unknown,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch(setUpdatingMappingId(mappingId));
        const codeId = code.id;
        dispatch(setEditMappingModalOpen(false));
        dispatch(editDaylightMappingLocal(codeId, payload));
        let url = `daylight/${practiceId}/codes/${codeId}/edit`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.post(url, payload);
      } catch (error) {
        displayActionFailedToastForCode('edit mapping', code);
        dispatch(updateCodeLocal(code));
      } finally {
        dispatch(setActionTargetCode({}));
        dispatch(setActionTargetMapping({}));
        dispatch(setUpdatingMappingId(''));
      }
    };
  };

  const editDaylightMappingWithComments = (
    practiceId: string,
    partnerId: string | null,
    code: Code,
    mappingId: string,
    payload: unknown,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch(setUpdatingMappingId(mappingId));
        const codeId = code.id;
        dispatch(setEditMappingModalOpen(false));
        dispatch(editDaylightMappingLocal(codeId, payload));
        let url = `daylight/${practiceId}/codes/${codeId}/editwithcomments`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.post(url, payload);
      } catch (error) {
        displayActionFailedToastForCode('edit mapping', code);
        dispatch(updateCodeLocal(code));
      } finally {
        dispatch(setActionTargetCode({}));
        dispatch(setActionTargetMapping({}));
        dispatch(setUpdatingMappingId(''));
      }
    };
  };

  const deleteDaylightMapping = (
    practiceId: string,
    partnerId: string | null,
    codes: Code[],
    targetCode: Code,
    targetMapping: Mapping,
    payload: unknown,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch(deleteDaylightMappingLocal(codes, targetCode, targetMapping));
        let url = `daylight/${practiceId}/codes/${targetCode.id}/delete`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.delete(url, { data: payload });
      } catch (error) {
        displayActionFailedToastForCode('delete mapping', targetCode);
        dispatch(updateCodeLocal(targetCode));
      }
    };
  };

  const exportDaylightCodes = (
    practiceId: string,
    partnerId: string | null,
    practiceName = `CodeExport.csv`,
  ) => {
    return async () => {
      try {
        let url = `daylight/${practiceId}/codes/export`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall({
          url: url,
          method: 'GET',
          responseType: 'blob',
        }).then(response => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', practiceName);
          document.body.appendChild(link);
          link.click();
        });
      } catch (error) {
        console.error((error as Error).message);
      }
    };
  };

  const markCodeComplete = (practiceId: string, code: Code, partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        const targetCodeId = code.id;
        dispatch(setUpdatingCodeId(targetCodeId));
        dispatch(markCodeCompleteLocal(targetCodeId));
        const payload = new FormData();
        payload.append('confirmationStatus', '1');
        let url = `daylight/${practiceId}/codes/${targetCodeId}/confirmationstatus`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.patch(url, payload);
      } catch (error) {
        displayActionFailedToastForCode('mark complete', code);
        dispatch(updateCodeLocal(code));
      } finally {
        dispatch(setUpdatingCodeId(''));
      }
    };
  };

  const unmarkCodeComplete = (practiceId: string, code: Code, partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        const targetCodeId = code.id;
        dispatch(setUpdatingCodeId(targetCodeId));
        dispatch(unmarkCodeCompleteLocal(targetCodeId));
        const payload = new FormData();
        payload.append('confirmationStatus', '0');
        let url = `daylight/${practiceId}/codes/${targetCodeId}/confirmationstatus`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.patch(url, payload);
      } catch (error) {
        displayActionFailedToastForCode('unmark complete', code);
        dispatch(updateCodeLocal(code));
      } finally {
        dispatch(setUpdatingCodeId(''));
      }
    };
  };

  const markCodeForReview = (practiceId: string, targetCode: Code, partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        const targetCodeId = targetCode.id;
        dispatch(setUpdatingCodeId(targetCodeId));
        dispatch(markCodeForReviewLocal(targetCodeId));
        let url = `daylight/${practiceId}/codes/${targetCodeId}/mark`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.post(url);
      } catch (error) {
        displayActionFailedToastForCode('mark for review', targetCode);
        dispatch(updateCodeLocal(targetCode));
      } finally {
        dispatch(setUpdatingCodeId(''));
      }
    };
  };

  const unmarkCodeForReview = (practiceId: string, targetCode: Code, partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        const targetCodeId = targetCode.id;
        dispatch(setUpdatingCodeId(targetCodeId));
        dispatch(unmarkCodeForReviewLocal(targetCodeId));
        let url = `daylight/${practiceId}/codes/${targetCodeId}/unmark`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.delete(url);
      } catch (error) {
        displayActionFailedToastForCode('confirm mappings', targetCode);
        dispatch(updateCodeLocal(targetCode));
      } finally {
        dispatch(setUpdatingCodeId(''));
      }
    };
  };

  const markCodeAsDisabled = (practiceId: string, targetCode: Code, partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        const targetCodeId = targetCode.id;
        dispatch(setUpdatingCodeId(targetCodeId));
        let url = `daylight/${practiceId}/codes/${targetCodeId}/disable`;
        if (partnerId) {
          url += `?partnerId=${partnerId}`;
        }
        await axiosCall.post(url);
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        const err = error as { response?: { status: number } };
        if (err.response?.status === 409) {
          showErrorToast('A disabled mapping already exists for this code.');
        } else {
          showErrorToast('Failed to disabled mapping. Please try again');
        }
      } finally {
        dispatch(setUpdatingCodeId(''));
      }
    };
  };

  const finalizePractice = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.post(`daylight/${practiceId}/finalize`);
        dispatch(setPracticeIsFinalized(true));
        dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        console.error((error as Error).message);
      }
    };
  };

  const unfinalizePractice = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosCall.delete(`daylight/${practiceId}/unfinalize`);
        dispatch(setPracticeIsFinalized(false));
        dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        console.error((error as Error).message);
      }
    };
  };

  const runCodeConverter = (id: string) => async () =>
    axiosCall.post('daylight/' + id + '/codeconverter');

  const fetchDaylightCodeUsageDetails = (practiceId: string) => {
    return async () => {
      try {
        const response = await axiosCall.get<{ data: unknown }>(
          `daylight/${practiceId}/codeusagedetails`,
        );
        return response.data;
      } catch (error) {
        console.error((error as Error).message);
        return [];
      }
    };
  };

  const setSelectedCodeUsageDetail = (codeUsageDetail: string) => ({
    type: SET_DAYLIGHT_SELECTED_CODE_USAGE_DETAIL,
    payload: codeUsageDetail,
  });

  const fetchDaylightUserHasAccessToUsageDetails = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      if (!practiceId) return;
      try {
        const response = await axiosCall.get<boolean>(
          `daylight/${practiceId}/codeusagedetails/hasaccess`,
        );
        dispatch(setDaylightUserHasAccessToUsageDetails(response.data));
        return response.data;
      } catch (error) {
        console.error((error as Error).message);
      }
    };
  };

  const setDaylightUserHasAccessToUsageDetails = (bool: boolean) => ({
    type: SET_DAYLIGHT_USER_HAS_ACCESS_TO_USAGE_DETAILS,
    payload: bool,
  });

  const setMappingCommentModalOpen = (bool: boolean) => ({
    type: SET_DAYLIGHT_MAPPING_COMMENT_MODAL_OPEN,
    payload: bool,
  });

  const updateCodeMappingComments = (
    practiceId: string,
    code: Code,
    codeMappingId: string,
    comments: string,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch(setUpdatingMappingId(codeMappingId));
        const codeId = code.id;
        if (codeId === undefined || codeMappingId === undefined) {
          showErrorToast(`Failed to update comments`);
          return;
        }
        const payload = { comments };
        dispatch(updateCodeMappingCommentsLocal(codeId, codeMappingId, comments));
        await axiosCall.post(
          `daylight/${practiceId}/codeMappings/${codeMappingId}/comments`,
          payload,
        );
      } catch (error) {
        displayActionFailedToastForCode('update comments', code);
        dispatch(updateCodeLocal(code));
      } finally {
        dispatch(setUpdatingMappingId(''));
      }
    };
  };

  const setDisplayedCodesCount = (count: number) => ({
    type: SET_DAYLIGHT_CODES_DISPLAYED_COUNT,
    payload: count,
  });

  const setSelectedPartnerId = (partnerId: string | null) => ({
    type: SET_DAYLIGHT_SELECTED_PARTNER_ID,
    payload: partnerId,
  });

  const clearSkipCodesTableScrollToTop = () => ({
    type: CLEAR_SKIP_CODES_TABLE_SCROLL_TO_TOP,
  });

  // Selection actions
  const selectCode = (codeId: string) => ({
    type: SELECT_DAYLIGHT_CODE,
    payload: codeId,
  });

  const deselectCode = (codeId: string) => ({
    type: DESELECT_DAYLIGHT_CODE,
    payload: codeId,
  });

  const selectAllFilteredCodes = (codeIds: string[]) => ({
    type: SELECT_ALL_FILTERED_CODES,
    payload: codeIds,
  });

  const clearCodeSelection = () => ({
    type: CLEAR_CODE_SELECTION,
  });

  const selectRangeOfCodes = (codeIds: string[]) => ({
    type: SELECT_ALL_FILTERED_CODES,
    payload: codeIds,
  });

  const setBulkAddMappingModalOpen = (bool: boolean) => ({
    type: SET_BULK_ADD_MAPPING_MODAL_OPEN,
    payload: bool,
  });

  // Bulk action thunks
  const bulkMarkCodesComplete = (
    practiceId: string,
    codeIds: string[],
    partnerId: string | null,
  ) => {
    return async (dispatch: Dispatch, getState: () => { daylight: State }) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });

        const state = getState();
        const codesNeedingReviewCleared = state.daylight.daylightCodes
          .filter(c => codeIds.includes(c.id) && c.needsReview)
          .map(c => c.id);

        if (codesNeedingReviewCleared.length > 0) {
          await axiosCall.post(`daylight/${practiceId}/bulk/unmark`, {
            codeIds: codesNeedingReviewCleared.map(Number),
            partnerId: partnerId || undefined,
          });
        }

        await axiosCall.patch(`daylight/${practiceId}/bulk/confirmationstatus`, {
          codeIds: codeIds.map(Number),
          confirmationStatus: 1,
          partnerId: partnerId || undefined,
        });
        toast.success(`Marked ${codeIds.length} codes as complete`);
        dispatch(clearCodeSelection());
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk mark codes as complete. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  const bulkUnmarkCodesComplete = (
    practiceId: string,
    codeIds: string[],
    partnerId: string | null,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });
        await axiosCall.patch(`daylight/${practiceId}/bulk/confirmationstatus`, {
          codeIds: codeIds.map(Number),
          confirmationStatus: 0,
          partnerId: partnerId || undefined,
        });
        toast.success(`Unmarked ${codeIds.length} codes from complete`);
        dispatch(clearCodeSelection());
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk unmark codes. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  const bulkMarkCodesForReview = (
    practiceId: string,
    codeIds: string[],
    partnerId: string | null,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });
        await axiosCall.post(`daylight/${practiceId}/bulk/mark`, {
          codeIds: codeIds.map(Number),
          partnerId: partnerId || undefined,
        });
        toast.success(`Marked ${codeIds.length} codes for review`);
        dispatch(clearCodeSelection());
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk mark codes for review. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  const bulkUnmarkCodesForReview = (
    practiceId: string,
    codeIds: string[],
    partnerId: string | null,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });
        await axiosCall.post(`daylight/${practiceId}/bulk/unmark`, {
          codeIds: codeIds.map(Number),
          partnerId: partnerId || undefined,
        });
        toast.success(`Confirmed mappings for ${codeIds.length} codes`);
        dispatch(clearCodeSelection());
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk confirm mappings. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  const bulkDisableCodes = (practiceId: string, codeIds: string[], partnerId: string | null) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });
        await axiosCall.post(`daylight/${practiceId}/bulk/disable`, {
          codeIds: codeIds.map(Number),
          partnerId: partnerId || undefined,
        });
        toast.success(`Disabled ${codeIds.length} codes`);
        dispatch(clearCodeSelection());
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk disable codes. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  const bulkAddMappingToCodes = (
    practiceId: string,
    codeIds: string[],
    mapping: unknown,
    partnerId: string | null,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: true });
        await axiosCall.post(`daylight/${practiceId}/bulk/addmapping`, {
          codeIds: codeIds.map(Number),
          mapping,
          partnerId: partnerId || undefined,
        });
        toast.success(`Added mapping to ${codeIds.length} codes`);
        dispatch(clearCodeSelection());
        dispatch(setBulkAddMappingModalOpen(false));
        await dispatch(refreshCodes() as unknown as Action);
      } catch (error) {
        showErrorToast('Failed to bulk add mapping. Please try again.');
      } finally {
        dispatch({ type: SET_BULK_ACTION_IN_PROGRESS, payload: false });
      }
    };
  };

  // Sort actions
  const setSortConfig = (sortConfig: SortConfig | null) => ({
    type: SET_DAYLIGHT_SORT,
    payload: sortConfig,
  });

  const toggleSort = (field: SortField) => {
    return (dispatch: Dispatch, getState: () => { daylight: State }) => {
      const current = getState().daylight.sortConfig;
      let next: SortConfig | null;
      if (current?.field === field) {
        next = current.direction === 'asc' ? { field, direction: 'desc' as SortDirection } : null;
      } else {
        next = { field, direction: 'asc' as SortDirection };
      }
      dispatch(setSortConfig(next));
    };
  };

  return {
    setDaylightCodeMapperPractice,
    addDaylightMapping,
    clearDaylightCodeMapperState,
    clearIncomingNeoCodes,
    deleteDaylightMapping,
    editDaylightMapping,
    exportDaylightCodes,
    fetchIncomingNeoCodes,
    finalizePractice,
    markCodeComplete,
    markCodeForReview,
    markCodeAsDisabled,
    runCodeConverter,
    toggleDaylightCodeReviewModal,
    unfinalizePractice,
    unmarkCodeComplete,
    unmarkCodeForReview,
    setCodeNameFilter,
    setCodeCategoryFilter,
    setCodeDescriptionFilter,
    setCodeLocationFilter,
    setCodeMappingFilter,
    setCodeHasCommentsFilter,
    setCodeIsMarkedCompletedFilter,
    setCodeNeedsReviewFilter,
    setActionTargetCode,
    setActionTargetMapping,
    setAddMappingModalOpen,
    setEditMappingModalOpen,
    refreshCodes,
    fetchDaylightCodeUsageDetails,
    setSelectedCodeUsageDetail,
    fetchDaylightUserHasAccessToUsageDetails,
    setMappingCommentModalOpen,
    updateCodeMappingComments,
    setDisplayedCodesCount,
    editDaylightMappingWithComments,
    setSelectedPartnerId,
    clearSkipCodesTableScrollToTop,
    selectCode,
    deselectCode,
    selectAllFilteredCodes,
    selectRangeOfCodes,
    clearCodeSelection,
    setBulkAddMappingModalOpen,
    bulkMarkCodesComplete,
    bulkUnmarkCodesComplete,
    bulkMarkCodesForReview,
    bulkUnmarkCodesForReview,
    bulkDisableCodes,
    bulkAddMappingToCodes,
    setSortConfig,
    toggleSort,
  };
};
